import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';
import { TZ } from '../lib/thTime.js';   // เวลาไทยเสมอ ไม่ตามนาฬิกาเครื่อง

// Admin (manager) user management — talks to the Pages Function /api/admin/users,
// which holds the service key and re-checks the caller's role server-side.
const ROLES = [
  ['purchasing', 'จัดซื้อ (purchasing)'],
  ['manager', 'ผู้จัดการ (manager)'],
  ['accounting', 'บัญชี (accounting)'],
  ['front_staff', 'หน้าร้าน (front_staff)'],
];
const roleTh = r => (ROLES.find(x => x[0] === r) ?? [r, r ?? '—'])[1];
const fmtDate = d => d
  ? new Date(d).toLocaleDateString('th-TH-u-ca-gregory', { timeZone: TZ, day: 'numeric', month: 'short', year: '2-digit' })
  : '—';

async function api(method, body) {
  const { data: { session } } = await supabase.auth.getSession();
  const res = await fetch('/api/admin/users', {
    method,
    headers: {
      Authorization: `Bearer ${session?.access_token ?? ''}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(j.error ?? `HTTP ${res.status}`);
  return j;
}

export default function Users() {
  const [users, setUsers] = useState(null);
  const [err, setErr] = useState('');
  const [notice, setNotice] = useState('');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ email: '', full_name: '', role: 'purchasing', password: '' });
  const [edit, setEdit] = useState(null); // user being edited { ...user, newPassword }
  const [busy, setBusy] = useState(false);

  async function load() {
    setErr('');
    try {
      const j = await api('GET');
      setUsers(j.users);
    } catch (e) {
      setErr(e.message);
      setUsers([]);
    }
  }
  useEffect(() => { load(); }, []);

  async function create() {
    if (busy) return;
    setBusy(true); setErr(''); setNotice('');
    try {
      await api('POST', form);
      setNotice(`สร้าง ${form.email} แล้ว — แจ้งรหัสผ่านเริ่มต้นให้เขาไปเปลี่ยนเองที่เมนู "รหัสผ่าน"`);
      setForm({ email: '', full_name: '', role: 'purchasing', password: '' });
      setCreating(false);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  async function saveEdit() {
    if (busy || !edit) return;
    setBusy(true); setErr(''); setNotice('');
    try {
      const body = { id: edit.id, role: edit.role, is_active: edit.is_active, full_name: edit.full_name };
      if (edit.newPassword) body.password = edit.newPassword;
      await api('PATCH', body);
      setNotice(`บันทึก ${edit.email} แล้ว${edit.newPassword ? ' (ตั้งรหัสผ่านใหม่ให้แล้ว)' : ''}`);
      setEdit(null);
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <div className="hero">
        <div className="eyebrow">ผู้ใช้ · Users</div>
        <h1>ใครเข้าระบบ<br />ได้แค่ไหน</h1>
        <p className="sub">
          สร้างผู้ใช้พร้อมกำหนดรหัสผ่านเริ่มต้น (เข้าได้ทันที ไม่ต้องยืนยันอีเมล) แล้วให้เจ้าตัวไปเปลี่ยนเอง
          ที่เมนู "รหัสผ่าน" · สิทธิ์ดูต้นทุน = จัดซื้อ / ผู้จัดการ / บัญชี — หน้าร้านล็อกอินได้แต่ไม่เห็นต้นทุน
        </p>
      </div>

      <div className="sup-toolbar">
        <span className="sup-count">{users ? `${users.length} คน` : 'กำลังโหลด…'}</span>
        <button className="add-sup" onClick={() => { setCreating(true); setErr(''); setNotice(''); }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="1.7"/></svg>
          เพิ่มผู้ใช้
        </button>
      </div>
      <p className="err-note">{err}</p>
      {notice && <p className="sd-empty" style={{ color: 'var(--ink)' }}>{notice}</p>}

      <div className="sup-list">
        {(users ?? []).map(u => (
          <div className="po-card" key={u.id} style={{ gridTemplateColumns: '1.2fr 1fr 150px 130px 44px' }}
               onClick={() => { setEdit({ ...u, newPassword: '' }); setErr(''); setNotice(''); }}>
            <div>
              <div className="po-ref" style={{ fontSize: 15, fontFamily: 'var(--body)', fontWeight: 700 }}>{u.full_name ?? '—'}</div>
              <div className="po-date mono">{u.email}</div>
            </div>
            <div className="po-date">เข้าล่าสุด {fmtDate(u.last_sign_in_at)}</div>
            <div><span className="tag">{roleTh(u.role)}</span></div>
            <div>{u.is_active
              ? <span className="po-chip ok">ใช้งานอยู่</span>
              : <span className="po-chip out">ปิดใช้งาน</span>}</div>
            <span className="arrow sm"><svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg></span>
          </div>
        ))}
      </div>

      {(creating || edit) && (
        <>
          <div className="scrim open" onClick={() => { setCreating(false); setEdit(null); }} />
          <aside className="drawer open">
            <div className="drawer-head">
              <div>
                <div className="eyebrow">{creating ? 'เพิ่มผู้ใช้ · New user' : 'แก้ไขผู้ใช้ · Edit user'}</div>
                <h2>{creating ? 'เพิ่มผู้ใช้' : (edit.full_name ?? edit.email)}</h2>
              </div>
              <button className="xbtn" onClick={() => { setCreating(false); setEdit(null); }} aria-label="ปิด">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
              </button>
            </div>
            <div className="drawer-body">
              {creating ? (
                <>
                  <div className="field">
                    <div className="lbl"><span className="th">อีเมล</span><span className="en">Email</span></div>
                    <input className="under" type="email" value={form.email}
                           onChange={e => setForm({ ...form, email: e.target.value })} />
                  </div>
                  <div className="field">
                    <div className="lbl"><span className="th">ชื่อ</span><span className="en">Full name</span></div>
                    <input className="under" value={form.full_name}
                           onChange={e => setForm({ ...form, full_name: e.target.value })} />
                  </div>
                  <div className="fx-row">
                    <div className="field">
                      <div className="lbl"><span className="th">Role</span></div>
                      <select className="under" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                        {ROLES.map(([k, th]) => <option key={k} value={k}>{th}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <div className="lbl"><span className="th">รหัสผ่านเริ่มต้น</span><span className="opt">อย่างน้อย 8 ตัว</span></div>
                      <input className="under mono" value={form.password}
                             onChange={e => setForm({ ...form, password: e.target.value })} />
                    </div>
                  </div>
                  <div className="cost-note" style={{ borderColor: 'var(--ink)' }}>
                    <span>ผู้ใช้ล็อกอินได้ทันทีด้วยรหัสนี้ แล้วเปลี่ยนเองได้ที่เมนู "รหัสผ่าน" มุมขวาบน</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="field">
                    <div className="lbl"><span className="th">ชื่อ</span></div>
                    <input className="under" value={edit.full_name ?? ''}
                           onChange={e => setEdit({ ...edit, full_name: e.target.value })} />
                  </div>
                  <div className="fx-row">
                    <div className="field">
                      <div className="lbl"><span className="th">Role</span></div>
                      <select className="under" value={edit.role ?? 'front_staff'}
                              onChange={e => setEdit({ ...edit, role: e.target.value })}>
                        {ROLES.map(([k, th]) => <option key={k} value={k}>{th}</option>)}
                      </select>
                    </div>
                    <div className="field">
                      <div className="lbl"><span className="th">สถานะ</span></div>
                      <select className="under" value={edit.is_active ? '1' : '0'}
                              onChange={e => setEdit({ ...edit, is_active: e.target.value === '1' })}>
                        <option value="1">ใช้งานอยู่</option>
                        <option value="0">ปิดใช้งาน</option>
                      </select>
                    </div>
                  </div>
                  <div className="field">
                    <div className="lbl"><span className="th">ตั้งรหัสผ่านใหม่ให้</span><span className="opt">เว้นว่าง = ไม่เปลี่ยน</span></div>
                    <input className="under mono" value={edit.newPassword}
                           onChange={e => setEdit({ ...edit, newPassword: e.target.value })}
                           placeholder="กรณีลืมรหัส" />
                  </div>
                </>
              )}
              <p className="err-note">{err}</p>
            </div>
            <div className="drawer-foot">
              <span className="txt">{creating ? 'สร้างผู้ใช้' : 'บันทึก'}</span>
              <button className="arrow" onClick={creating ? create : saveEdit} disabled={busy} aria-label="บันทึก">
                <svg viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H9M17 7v8" stroke="#0A0A0A" strokeWidth="1.6"/></svg>
              </button>
            </div>
          </aside>
        </>
      )}
    </section>
  );
}
